import React, { useState } from 'react';

// Define types for our component
interface SqlToApiProps { }

// Define database types
type ColumnDefinition = {
    name: string;
    type: string;
};

type DatabaseTable = {
    name: string;
    columns: ColumnDefinition[];
    rows: any[][];
};

// API response types
interface ApiResponse {
    success: boolean;
    data?: any;
    meta?: {
        count: number;
        totalCount?: number;
        page?: number;
        totalPages?: number;
    };
    error?: string;
    query?: string;
    executionTime?: number;
}

// Mock database for demo purposes
const mockDatabase: Record<string, DatabaseTable> = {
    users: {
        name: 'users',
        columns: [
            { name: 'id', type: 'number' },
            { name: 'name', type: 'string' },
            { name: 'email', type: 'string' },
            { name: 'created_at', type: 'date' }
        ],
        rows: [
            [1, 'John Doe', 'john@example.com', '2023-01-15'],
            [2, 'Jane Smith', 'jane@example.com', '2023-02-20'],
            [3, 'Bob Johnson', 'bob@example.com', '2023-03-10'],
            [4, 'Alice Brown', 'alice@example.com', '2023-04-05'],
            [5, 'Charlie Davis', 'charlie@example.com', '2023-05-12']
        ]
    },
    products: {
        name: 'products',
        columns: [
            { name: 'id', type: 'number' },
            { name: 'name', type: 'string' },
            { name: 'price', type: 'number' },
            { name: 'category', type: 'string' },
            { name: 'in_stock', type: 'boolean' }
        ],
        rows: [
            [101, 'Laptop', 1299.99, 'Electronics', true],
            [102, 'Smartphone', 899.99, 'Electronics', true],
            [103, 'Headphones', 199.99, 'Electronics', false],
            [104, 'Desk Chair', 249.99, 'Furniture', true],
            [105, 'Coffee Table', 349.99, 'Furniture', true],
            [106, 'Bookshelf', 199.99, 'Furniture', false],
            [107, 'Water Bottle', 24.99, 'Kitchen', true],
            [108, 'Blender', 89.99, 'Kitchen', true]
        ]
    },
    orders: {
        name: 'orders',
        columns: [
            { name: 'id', type: 'number' },
            { name: 'user_id', type: 'number' },
            { name: 'total', type: 'number' },
            { name: 'status', type: 'string' },
            { name: 'order_date', type: 'date' }
        ],
        rows: [
            [1001, 1, 1299.99, 'Delivered', '2023-06-15'],
            [1002, 2, 899.99, 'Shipped', '2023-06-20'],
            [1003, 3, 249.99, 'Processing', '2023-06-25'],
            [1004, 1, 89.99, 'Delivered', '2023-07-01'],
            [1005, 4, 549.98, 'Shipped', '2023-07-05'],
            [1006, 5, 199.99, 'Processing', '2023-07-10']
        ]
    }
};

// Simple SQL parser function
const parseSql = (sqlQuery: string, database: Record<string, DatabaseTable>) => {
    sqlQuery = sqlQuery.trim();

    // Basic SELECT statement parsing
    if (sqlQuery.toLowerCase().startsWith('select')) {
        // Very basic parsing - not a production-ready SQL parser
        const selectMatch = sqlQuery.match(/select\s+(.*?)\s+from\s+(\w+)(?:\s+where\s+(.*?))?(?:\s+order\s+by\s+(.*?))?(?:\s+limit\s+(\d+))?;?\s*$/i);

        if (!selectMatch) {
            throw new Error('Invalid SQL query format. Expected: SELECT columns FROM table [WHERE condition] [ORDER BY columns] [LIMIT number]');
        }

        const columns = selectMatch[1].trim();
        const tableName = selectMatch[2].trim();
        const whereClause = selectMatch[3] ? selectMatch[3].trim() : null;
        const orderByClause = selectMatch[4] ? selectMatch[4].trim() : null;
        const limitClause = selectMatch[5] ? parseInt(selectMatch[5].trim()) : null;

        // Check if table exists
        if (!database[tableName]) {
            throw new Error(`Table '${tableName}' does not exist`);
        }

        const table = database[tableName];

        // Parse columns
        let selectedColumns: number[] = [];
        let columnNames: string[] = [];

        if (columns === '*') {
            // Select all columns
            selectedColumns = table.columns.map((_, index) => index);
            columnNames = table.columns.map(col => col.name);
        } else {
            // Select specific columns
            const colList = columns.split(',').map(col => col.trim());

            colList.forEach(colName => {
                const colIndex = table.columns.findIndex(col => col.name === colName);
                if (colIndex === -1) {
                    throw new Error(`Column '${colName}' does not exist in table '${tableName}'`);
                }
                selectedColumns.push(colIndex);
                columnNames.push(colName);
            });
        }

        // Filter rows based on WHERE clause (very basic implementation)
        let filteredRows = [...table.rows];

        if (whereClause) {
            // This is a very simplistic WHERE parser that only supports basic equality conditions
            const whereMatch = whereClause.match(/(\w+)\s*(=|!=|>|<|>=|<=)\s*([^;]*)/);

            if (whereMatch) {
                const whereColumn = whereMatch[1].trim();
                const whereOperator = whereMatch[2].trim();
                let whereValue = whereMatch[3].trim();

                // Remove quotes from string values
                if ((whereValue.startsWith("'") && whereValue.endsWith("'")) ||
                    (whereValue.startsWith('"') && whereValue.endsWith('"'))) {
                    whereValue = whereValue.substring(1, whereValue.length - 1);
                }

                const colIndex = table.columns.findIndex(col => col.name === whereColumn);

                if (colIndex === -1) {
                    throw new Error(`Column '${whereColumn}' does not exist in table '${tableName}'`);
                }

                filteredRows = filteredRows.filter(row => {
                    const cellValue = row[colIndex];

                    switch (whereOperator) {
                        case '=':
                            return cellValue == whereValue; // Using loose equality for different types
                        case '!=':
                            return cellValue != whereValue;
                        case '>':
                            return cellValue > whereValue;
                        case '<':
                            return cellValue < whereValue;
                        case '>=':
                            return cellValue >= whereValue;
                        case '<=':
                            return cellValue <= whereValue;
                        default:
                            return false;
                    }
                });
            } else {
                throw new Error('Invalid WHERE clause format');
            }
        }

        // Apply ORDER BY (very basic implementation)
        if (orderByClause) {
            const orderParts = orderByClause.split(' ');
            const orderColumn = orderParts[0].trim();
            const isDesc = orderParts.length > 1 && orderParts[1].trim().toLowerCase() === 'desc';

            const colIndex = table.columns.findIndex(col => col.name === orderColumn);

            if (colIndex === -1) {
                throw new Error(`Column '${orderColumn}' does not exist in table '${tableName}'`);
            }

            filteredRows.sort((a, b) => {
                const valueA = a[colIndex];
                const valueB = b[colIndex];

                if (valueA < valueB) return isDesc ? 1 : -1;
                if (valueA > valueB) return isDesc ? -1 : 1;
                return 0;
            });
        }

        // Apply LIMIT
        if (limitClause !== null && !isNaN(limitClause)) {
            filteredRows = filteredRows.slice(0, limitClause);
        }

        // Convert to array of objects with column names as keys
        const resultData = filteredRows.map(row => {
            const obj: Record<string, any> = {};
            selectedColumns.forEach((colIndex, i) => {
                const colName = columnNames[i];
                const colType = table.columns[colIndex].type;
                let value = row[colIndex];

                // Type conversion for API response
                if (colType === 'number' && typeof value === 'string') {
                    value = parseFloat(value);
                } else if (colType === 'boolean' && typeof value === 'string') {
                    value = value.toLowerCase() === 'true';
                }

                obj[colName] = value;
            });
            return obj;
        });

        return {
            columns: columnNames,
            rows: filteredRows,
            data: resultData,
            count: resultData.length,
            totalCount: table.rows.length
        };
    } else {
        throw new Error('Only SELECT queries are supported');
    }
};

// Generate API response
const generateApiResponse = (
    queryResults: {
        columns: string[];
        rows: any[][];
        data: Record<string, any>[];
        count: number;
        totalCount: number;
    },
    options: {
        format: 'standard' | 'graphql' | 'rest' | 'openapi';
        includeMetadata: boolean;
        includePagination: boolean;
        page?: number;
        pageSize?: number;
        includeQuery?: boolean;
        query?: string;
    },
    executionTime: number
): ApiResponse => {
    const {
        format,
        includeMetadata,
        includePagination,
        page = 1,
        pageSize,
        includeQuery,
        query
    } = options;

    let responseData: ApiResponse = {
        success: true,
        data: queryResults.data,
        executionTime: executionTime
    };

    if (includeMetadata) {
        responseData.meta = {
            count: queryResults.count,
            totalCount: queryResults.totalCount
        };

        if (includePagination) {
            const calculatedPageSize = pageSize || queryResults.count;
            const totalPages = Math.ceil(queryResults.totalCount / calculatedPageSize);

            responseData.meta.page = page;
            responseData.meta.totalPages = totalPages;
        }
    }

    if (includeQuery && query) {
        responseData.query = query;
    }

    // Format adjustments based on API style
    switch (format) {
        case 'graphql':
            // Simulate GraphQL-style response
            responseData = {
                success: true,
                data: {
                    result: queryResults.data,
                    metadata: includeMetadata ? {
                        count: queryResults.count,
                        totalCount: queryResults.totalCount,
                        ...(includePagination ? {
                            page,
                            totalPages: Math.ceil(queryResults.totalCount / (pageSize || queryResults.count))
                        } : {})
                    } : undefined
                }
            };
            break;

        case 'openapi':
            // OpenAPI-style response
            responseData = {
                success: true,
                data: queryResults.data,
                meta: includeMetadata ? {
                    count: queryResults.count,
                    totalCount: queryResults.totalCount,
                    ...(includePagination ? {
                        page,
                        totalPages: Math.ceil(queryResults.totalCount / (pageSize || queryResults.count))
                    } : {})
                } : undefined,
                ...(includeQuery && query ? { query } : {})
            };
            break;

        // 'rest' is the default standard format
        default:
            // Already structured correctly above
            break;
    }

    return responseData;
};

// Main component to convert SQL to API
const SqlToApi: React.FC<SqlToApiProps> = () => {
    // State for the SQL input
    const [sqlInput, setSqlInput] = useState<string>('');
    // State for the API response output
    const [apiResponse, setApiResponse] = useState<string>('');
    // State for error messages
    const [error, setError] = useState<string>('');
    // State for loading indicator
    const [isLoading, setIsLoading] = useState<boolean>(false);
    // State for showing database schema
    const [showSchema, setShowSchema] = useState<boolean>(false);
    // State to track if the conversion happened
    const [isApiGenerated, setIsApiGenerated] = useState<boolean>(false);
    // State for copied indicator
    const [copied, setCopied] = useState<boolean>(false);

    // API response options
    const [format, setFormat] = useState<'standard' | 'graphql' | 'rest' | 'openapi'>('standard');
    const [includeMetadata, setIncludeMetadata] = useState<boolean>(true);
    const [includePagination, setIncludePagination] = useState<boolean>(false);
    const [includeQuery, setIncludeQuery] = useState<boolean>(true);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [prettyPrint, setPrettyPrint] = useState<boolean>(true);

    // Handle SQL input change
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setSqlInput(e.target.value);
        setError('');
        setIsApiGenerated(false);
    };

    // Use example query
    const useExampleQuery = () => {
        const exampleQuery = 'SELECT * FROM products WHERE category = "Electronics";';
        setSqlInput(exampleQuery);
        setError('');
        setIsApiGenerated(false);
        setApiResponse('');
    };

    // Convert SQL to API Response
    const convertSqlToApi = () => {
        setError('');
        setIsLoading(true);
        setIsApiGenerated(false);
        setApiResponse('');

        if (!sqlInput.trim()) {
            setError('Please enter an SQL query');
            setIsLoading(false);
            return;
        }

        try {
            const startTime = performance.now();

            // Parse the SQL query and execute it against our mock database
            const results = parseSql(sqlInput, mockDatabase);

            const endTime = performance.now();
            const executionTime = endTime - startTime;

            // Generate the API response
            const options = {
                format,
                includeMetadata,
                includePagination,
                page,
                pageSize: includePagination ? pageSize : undefined,
                includeQuery,
                query: includeQuery ? sqlInput : undefined
            };

            const response = generateApiResponse(results, options, executionTime);

            // Format the response
            const formattedResponse = prettyPrint
                ? JSON.stringify(response, null, 2)
                : JSON.stringify(response);

            setApiResponse(formattedResponse);
            setIsApiGenerated(true);
            setIsLoading(false);
        } catch (e) {
            setError(`Error executing SQL query: ${e instanceof Error ? e.message : String(e)}`);
            setIsLoading(false);
        }
    };

    // Clear all inputs and reset states
    const clearAll = () => {
        setSqlInput('');
        setApiResponse('');
        setError('');
        setIsApiGenerated(false);
    };

    // Copy API response to clipboard
    const copyToClipboard = () => {
        navigator.clipboard.writeText(apiResponse)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(() => {
                setError('Failed to copy to clipboard');
            });
    };

    // Render database schema
    const renderDatabaseSchema = () => {
        return (
            <div className="bg-blue-50 p-4 rounded-md border border-blue-100 text-sm">
                <h3 className="font-medium mb-2">Database Schema</h3>
                {Object.values(mockDatabase).map(table => (
                    <div key={table.name} className="mb-3">
                        <div className="font-medium">{table.name}</div>
                        <div className="pl-4">
                            {table.columns.map((col, index) => (
                                <div key={index}>
                                    {col.name} ({col.type})
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
                <p className="mt-3 text-blue-800">
                    This is a mock database for demonstration. In a real application, you would
                    connect to an actual database or database API.
                </p>
            </div>
        );
    };

    return (
        <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">SQL to API Converter</h1>

            {/* Description */}
            <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
                <p className="text-blue-800">
                    Execute SQL queries and convert the results to formatted API responses. This demo uses a mock in-memory database with sample data.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Input Section */}
                <div className="flex flex-col h-full">
                    <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
                        <div className="flex justify-between items-center mb-3">
                            <label htmlFor="sqlInput" className="font-medium text-gray-700">
                                SQL Query
                            </label>
                            <div className="flex space-x-2">
                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800"
                                    onClick={useExampleQuery}
                                >
                                    Use Example
                                </button>
                                <button
                                    className="text-sm text-gray-600 hover:text-gray-800"
                                    onClick={() => setSqlInput('')}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>

                        <textarea
                            id="sqlInput"
                            className="w-full h-64 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                            value={sqlInput}
                            onChange={handleInputChange}
                            placeholder="Enter your SQL query here, e.g.,
SELECT * FROM users WHERE created_at > '2023-01-01';"
                        />

                        <div className="mt-3">
                            <button
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                onClick={() => setShowSchema(!showSchema)}
                            >
                                <span>{showSchema ? 'Hide' : 'Show'} Database Schema</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`h-4 w-4 ml-1 transition-transform ${showSchema ? 'transform rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                        </div>

                        {showSchema && (
                            <div className="mt-3">
                                {renderDatabaseSchema()}
                            </div>
                        )}
                    </div>

                    {/* Configuration */}
                    <div className="bg-white p-4 rounded-md shadow-sm">
                        <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                            <div>
                                <label htmlFor="format" className="block text-sm font-medium text-gray-700 mb-1">
                                    API Response Format
                                </label>
                                <select
                                    id="format"
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    value={format}
                                    onChange={(e) => setFormat(e.target.value as 'standard' | 'graphql' | 'rest' | 'openapi')}
                                >
                                    <option value="standard">Standard</option>
                                    <option value="rest">REST</option>
                                    <option value="graphql">GraphQL</option>
                                    <option value="openapi">OpenAPI</option>
                                </select>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="includeMetadata"
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    checked={includeMetadata}
                                    onChange={(e) => setIncludeMetadata(e.target.checked)}
                                />
                                <label htmlFor="includeMetadata" className="ml-2 block text-sm text-gray-700">
                                    Include Metadata
                                </label>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="includePagination"
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    checked={includePagination}
                                    onChange={(e) => setIncludePagination(e.target.checked)}
                                    disabled={!includeMetadata}
                                />
                                <label htmlFor="includePagination" className={`ml-2 block text-sm ${!includeMetadata ? 'text-gray-400' : 'text-gray-700'}`}>
                                    Include Pagination
                                </label>
                            </div>

                            {includePagination && includeMetadata && (
                                <div className="pl-6 space-y-2">
                                    <div>
                                        <label htmlFor="page" className="block text-sm text-gray-700 mb-1">
                                            Page
                                        </label>
                                        <input
                                            type="number"
                                            id="page"
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            value={page}
                                            onChange={(e) => setPage(parseInt(e.target.value) || 1)}
                                            min={1}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="pageSize" className="block text-sm text-gray-700 mb-1">
                                            Page Size
                                        </label>
                                        <input
                                            type="number"
                                            id="pageSize"
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            value={pageSize}
                                            onChange={(e) => setPageSize(parseInt(e.target.value) || 10)}
                                            min={1}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="includeQuery"
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    checked={includeQuery}
                                    onChange={(e) => setIncludeQuery(e.target.checked)}
                                />
                                <label htmlFor="includeQuery" className="ml-2 block text-sm text-gray-700">
                                    Include Original Query
                                </label>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="prettyPrint"
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    checked={prettyPrint}
                                    onChange={(e) => setPrettyPrint(e.target.checked)}
                                />
                                <label htmlFor="prettyPrint" className="ml-2 block text-sm text-gray-700">
                                    Pretty Print JSON
                                </label>
                            </div>
                        </div>

                        <div className="mt-4 flex justify-between">
                            <button
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                onClick={convertSqlToApi}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <div className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Processing...
                                    </div>
                                ) : (
                                    'Generate API Response'
                                )}
                            </button>

                            <button
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                onClick={clearAll}
                            >
                                Clear All
                            </button>
                        </div>
                    </div>
                </div>

                {/* Output Section */}
                <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
                    <div className="flex justify-between items-center mb-3">
                        <label className="font-medium text-gray-700">
                            API Response
                        </label>

                        {isApiGenerated && (
                            <div className="flex space-x-2">
                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                    onClick={copyToClipboard}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 mr-1"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    {copied ? 'Copied!' : 'Copy JSON'}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="flex-grow flex flex-col">
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                                <p className="font-medium">Error</p>
                                <p>{error}</p>
                            </div>
                        )}

                        {!error && !isApiGenerated && (
                            <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                                <div className="text-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <p>
                                        API response will appear here after executing the SQL query.
                                    </p>
                                </div>
                            </div>
                        )}

                        {!error && isApiGenerated && (
                            <div className="flex-grow flex flex-col">
                                <div className="flex-grow flex flex-col">
                                    <textarea
                                        id="apiOutput"
                                        className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
                                        value={apiResponse}
                                        readOnly
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    {isApiGenerated && (
                        <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
                            <div className="flex items-center mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="font-medium">SQL Results Successfully Converted to API Format!</p>
                            </div>
                            <p className="text-sm">
                                You can now copy the generated JSON response. This simulates how a real database API would return results.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Information Section */}
            <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
                <h2 className="text-lg font-semibold mb-4">About SQL to API Conversion</h2>

                <div className="space-y-4">
                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">What This Tool Does</h3>
                        <p className="text-gray-600">
                            This SQL to API converter allows you to execute SQL queries against a mock database and convert the results to formatted JSON API responses. In a production environment, this would connect to a real database and API service. This demo provides a simulation of that workflow.
                        </p>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Features</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><strong>SQL Query Execution</strong> - Execute SELECT queries against the mock database</li>
                            <li><strong>Multiple API Formats</strong> - Choose between standard, REST, GraphQL, and OpenAPI response formats</li>
                            <li><strong>Metadata Controls</strong> - Include count and pagination information</li>
                            <li><strong>Pagination Support</strong> - Configure page number and page size</li>
                            <li><strong>Original Query</strong> - Option to include the original SQL query in the response</li>
                            <li><strong>Performance Metrics</strong> - Includes execution time in the response</li>
                            <li><strong>Pretty Print</strong> - Format JSON for readability</li>
                            <li><strong>Copy to Clipboard</strong> - Easily copy the API response</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">API Format Options</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li>
                                <strong>Standard</strong> - A simple, clean response format with data and metadata
                            </li>
                            <li>
                                <strong>REST</strong> - Common REST API style with resources and metadata
                            </li>
                            <li>
                                <strong>GraphQL</strong> - Structured like a GraphQL response with nested data and metadata
                            </li>
                            <li>
                                <strong>OpenAPI</strong> - Follows OpenAPI specification patterns for responses
                            </li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">When to Use API Responses</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><strong>Frontend Applications</strong> - Provide data to web or mobile apps</li>
                            <li><strong>Microservices</strong> - Exchange data between different services</li>
                            <li><strong>Data Integration</strong> - Connect systems that need to share information</li>
                            <li><strong>API Development</strong> - Design and test API response formats</li>
                            <li><strong>Analytics Tools</strong> - Provide data for reporting and analysis</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Limitations of This Demo</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li>Uses a mock in-memory database with predefined data</li>
                            <li>Only supports basic SELECT queries with simple WHERE clauses</li>
                            <li>Limited SQL syntax support (no JOINs, GROUP BY, HAVING, etc.)</li>
                            <li>No support for parameterized queries</li>
                            <li>No authentication or authorization mechanisms</li>
                            <li>No rate limiting or caching</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Supported SQL Features</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><strong>SELECT columns</strong> - Specify columns to select, or use * for all columns</li>
                            <li><strong>FROM table</strong> - Select data from available tables (users, products, orders)</li>
                            <li><strong>WHERE condition</strong> - Basic comparison operators</li>
                            <li><strong>ORDER BY column</strong> - Sort by a column (ASC or DESC)</li>
                            <li><strong>LIMIT number</strong> - Limit the number of results</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SqlToApi;